import React, { useState } from "react";
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import produce from "immer";
import { initialState } from "./initialState";

export const anteriorSortArray = ["Lids", "Cornea", "Conjunctiva", "A/C", "Pupil/Iris", "Lens"];
export const anteriorSegmentHeaders = ["Anterior Segment", "R-Eye", "L-Eye"];

interface AnteriorSegmentProps {
  segmentData: typeof initialState;
  onSegmentChange: (updatedData: typeof initialState) => void;
}

const COLS = [
  { key: "rEye", label: "R-Eye" },
  { key: "lEye", label: "L-Eye" }
] as const;

export default function AnteriorSegment({
  segmentData,
  onSegmentChange
}: AnteriorSegmentProps): React.JSX.Element {
  const [newRowLabel, setNewRowLabel] = useState("");

  const handleChange = (row: string, col: string, value: string) => {
    const updated = produce(segmentData, (draft: typeof initialState) => {
      if (!draft.anteriorSegment) {
        draft.anteriorSegment = JSON.parse(JSON.stringify(initialState.anteriorSegment));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rowData = draft.anteriorSegment[
        row as keyof typeof draft.anteriorSegment
      ] as unknown as Record<string, string | null>;
      if (rowData) {
        rowData[col] = value === "" ? null : value;
      }
    });
    onSegmentChange(updated);
  };

  const handleAddNewRow = () => {
    if (newRowLabel.trim() === "") return;
    const updated = produce(segmentData, (draft: typeof initialState) => {
      const anterior = draft.anteriorSegment as Record<
        string,
        { rEye: string | null; lEye: string | null }
      >;
      anterior[newRowLabel] = { rEye: null, lEye: null };
    });
    onSegmentChange(updated);
    setNewRowLabel("");
  };

  const currentAnteriorSegment = segmentData?.anteriorSegment || {};

  const sortRows = (keys: string[]) =>
    [...keys].sort((a, b) => {
      const indexA = anteriorSortArray.indexOf(a);
      const indexB = anteriorSortArray.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  const rows = sortRows(Object.keys(currentAnteriorSegment));

  const cellStyle = {
    border: 1,
    borderColor: "divider"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const
  };

  return (
    <Box>
      <TableContainer>
        <Typography
          sx={{
            padding: 1,
            textAlign: "center",
            backgroundColor: "#ededed",
            borderLeft: 1,
            borderRight: 1,
            borderTop: 1,
            borderColor: "divider"
          }}
        >
          Anterior Segment
        </Typography>

        <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: "150px" }} />
              {COLS.map((col) => (
                <TableCell key={col.key} sx={headerCellStyle}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row}>
                <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{row}</TableCell>
                {COLS.map((col) => (
                  <TableCell key={col.key} sx={cellStyle}>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={
                        (currentAnteriorSegment as Record<string, Record<string, string | null>>)[
                          row
                        ]?.[col.key] ?? ""
                      }
                      onChange={(e) => handleChange(row, col.key, e.target.value)}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "4px 8px",
                          fontSize: "0.875rem"
                        }
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          size="small"
          placeholder="Add new"
          value={newRowLabel}
          onChange={(e) => setNewRowLabel(e.target.value)}
          sx={{ width: "150px" }}
        />
        <IconButton onClick={handleAddNewRow} color="primary" size="small">
          <AddCircleOutlineIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
